import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../redux/reducers';
import { isOrgOwner } from '../utils/constants';

/**
 * Route guard for organization-owner pages.
 *
 * AuthGuard only verifies login. The sider already hides owner menus for
 * technicians, but those routes were still reachable by typing the URL.
 * This guard closes that gap: non-owners are sent to /work-order.
 *
 * Role comes from Redux `userInfo.role` (hydrated via /api/users/sync and
 * persisted in localStorage), matching how the sider decides visibility.
 */
type OwnerGuardProps = {
    children: React.ReactElement;
};

export const OwnerGuard: React.FC<OwnerGuardProps> = ({ children }) => {
    const navigate = useNavigate();
    const role = useSelector((state: RootState) => state.userInfo?.role);
    const allowed = isOrgOwner(role);

    useEffect(() => {
        if (!allowed) {
            navigate('/work-order', { replace: true });
        }
    }, [allowed, navigate]);

    if (!allowed) {
        return null;
    }

    return children;
};
